import { inject, Injectable } from '@angular/core';
import { SchoolV4 } from '@lx/shared/data-access/school';
import { Country } from '@lx/shared/data-access/types';
import { sortArray } from '@lx/shared/utils-array';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CourseStore, QuoteToolLiteStore, SharedDataStore } from '../store';
import { CountryWeight, GroupedSchoolLocation } from '../types';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

/**
 * Service to manage schools
 */
@Injectable()
export class SchoolLocationService {
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly courseStore = inject(CourseStore);

  /**
   * Get the school locations grouped by country
   *
   * @returns the observable with the grouped school locations or an empty array if no language or location type is provided
   */
  getGroupedSchoolLocations(): Observable<GroupedSchoolLocation[]> {
    return combineLatest([this.courseStore.selectedLanguage$, this.quoteToolLiteStore.activeFlow$]).pipe(
      switchMap(([selectedLanguage, activeFlow]: [string, Flows]) => {
        if (!selectedLanguage || !activeFlow) {
          return of([]);
        }
        return this.getInSchoolGroupedSchoolLocations(selectedLanguage, activeFlow);
      }),
    );
  }

  private getInSchoolGroupedSchoolLocations(language: string, flow: Flow) {
    return combineLatest([this.sharedDataStore.schools$, this.sharedDataStore.countries$, this.sharedDataStore.countryWeights$]).pipe(
      map(([schools, countries, countryWeights]: [SchoolV4[], Country[], CountryWeight[]]) =>
        this.prepareGroupedSchoolLocations(language, schools, countries, countryWeights, flow),
      ),
    );
  }

  /**
   * Prepares the grouped school locations from the list of schools and countries. By getting only the schools
   * for the specified language and removing from the list of schools the online ones.
   *
   * The grouped school locations are sorted by country weight and the school locations are sorted alphabetically by name (asc).
   */
  private prepareGroupedSchoolLocations(
    language: string,
    schools: SchoolV4[],
    countries: Country[],
    countryWeights: CountryWeight[],
    flow: Flow,
  ): GroupedSchoolLocation[] {
    // If there are no schools or countries, return an empty array
    if (!schools?.length || !countries?.length) {
      return [];
    }

    // Filter schools by type and language
    const schoolType = flow.toUpperCase();
    const languageSchools = schools
      ?.filter(({ types }) => types.find((type) => type === schoolType))
      .filter(({ courseLanguages }) => {
        const typeCourseLanguages = courseLanguages[schoolType] ?? [];
        return typeCourseLanguages.find((courseLanguage) => courseLanguage === language);
      });

    // Sort countries by weight
    const sortedCountries = this.sortCountries(countries, countryWeights, language, flow);

    // Remove from the schools the online schools
    const schoolsWithoutOnlineSchools = languageSchools?.filter(({ isOnline }) => !isOnline);

    const groupedCountrySchoolLocations = [];

    // Create the grouped school locations
    sortedCountries.forEach(({ code: countryCode, name: countryName, weight }) => {
      // Filter the schools for the country, prepare the grouped school locations and sort them alphabetically by name (asc)
      const countrySchoolLocations = sortArray(
        schoolsWithoutOnlineSchools
          .filter(({ country: { code: countrySchool } }) => countrySchool === countryCode)
          .map(({ code, name: schoolName, ageMin, ageMax }) => {
            const fullSchoolName = flow === Flows.Junior ? `${schoolName} (${ageMin}-${ageMax})` : schoolName;

            return {
              value: code,
              label: fullSchoolName,
            };
          }),
        [{ property: 'label' }],
      );

      // If there are schools for the country, add the grouped school location to the list of grouped school locations
      if (countrySchoolLocations.length > 0) {
        groupedCountrySchoolLocations.push({
          value: countryCode,
          label: countryName,
          weight: weight,
          items: countrySchoolLocations,
        });
      }
    });

    return groupedCountrySchoolLocations;
  }

  /**
   * Sort the countries from the weights that are specified for the QT
   *
   * @param countries the list of countries
   * @param countryWeights the list of country weights for the QT
   * @param language the language used for taking the proper weight
   * @param flow the flow used for taking the proper weight
   * @private
   */
  private sortCountries(countries: Country[], countryWeights: CountryWeight[], language: string, flow: Flow): Country[] {
    // Prepare an array of countries whose weights will be the ones specified for the QT. These values are taken from the countryWeights array.
    const countriesWithQTWeight = [...countries];
    countriesWithQTWeight.forEach((country) => {
      const weights = countryWeights.find((countryWeight) => countryWeight.code === country.code)?.weights;
      if (weights) {
        const weight = weights.find(({ language: weightLanguage, flow: weightFlow }) => weightLanguage === language && weightFlow === flow);
        country.weight = weight?.weight ?? country.weight;
      }
    });

    return sortArray(countriesWithQTWeight, [{ property: 'weight' }]);
  }
}
